import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { createUploadPresign, useCreateFeedback, useRolePermissions, type FeedbackMetadata } from "@/lib/api-extra";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogClose, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { showErrorToast, showSuccessToast } from "@/lib/toast-utils";
import { MessageSquarePlus, RefreshCw, X, Send, ImagePlus, Camera } from "lucide-react";
import { captureFeedbackMetadata } from "@/lib/feedback-capture";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS, ROLE_PERMISSIONS } from "@/lib/role-permissions";
import type { AnyRole } from "@/lib/roles";

type Tipo = "bug" | "mejora" | "observacion";
type Prioridad = "alta" | "media" | "baja";
type FloatingPosition = { x: number; y: number };

const MAX_ATTACHMENTS = 3;
const FEEDBACK_BUTTON_SIZE = 32;
const FEEDBACK_BUTTON_MARGIN = 16;
const FEEDBACK_DRAG_THRESHOLD = 6;
const FEEDBACK_POSITION_STORAGE_KEY = "feedback-button-position";

const TIPO_OPTIONS: { value: Tipo; label: string }[] = [
  { value: "bug", label: "Bug / Error" },
  { value: "mejora", label: "Mejora" },
  { value: "observacion", label: "Observación" },
];

const PRIORIDAD_OPTIONS: { value: Prioridad; label: string; color: string }[] = [
  { value: "alta", label: "Alta", color: "bg-red-500" },
  { value: "media", label: "Media", color: "bg-amber-400" },
  { value: "baja", label: "Baja", color: "bg-emerald-500" },
];

function compressImage(file: File, maxWidth = 1280, quality = 0.72): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("No se pudo leer la imagen")); };
    img.src = url;
  });
}

function extensionForContentType(contentType: string): string {
  if (contentType === "image/png") return ".png";
  if (contentType === "image/webp") return ".webp";
  return ".jpg";
}

async function dataUrlToFile(dataUrl: string, basename: string): Promise<File> {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const contentType = blob.type || "image/jpeg";
  const ext = extensionForContentType(contentType);
  return new File([blob], `${basename}${ext}`, { type: contentType });
}

async function uploadImageDataUrl(dataUrl: string, basename: string): Promise<string> {
  const file = await dataUrlToFile(dataUrl, basename);
  const { uploadUrl, fileUrl } = await createUploadPresign({
    filename: file.name,
    contentType: file.type,
    folder: "feedback",
  });

  const putRes = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!putRes.ok) {
    throw new Error(`No se pudo subir imagen (${putRes.status})`);
  }

  return fileUrl;
}

function appendCaptureError(metadata: FeedbackMetadata, message: string) {
  const previous = metadata.captureError?.trim();
  metadata.captureError = previous ? `${previous} | ${message}` : message;
}

function getDefaultFloatingPosition(): FloatingPosition {
  return {
    x: window.innerWidth - FEEDBACK_BUTTON_SIZE - FEEDBACK_BUTTON_MARGIN,
    y: window.innerHeight - FEEDBACK_BUTTON_SIZE - FEEDBACK_BUTTON_MARGIN,
  };
}

function clampFloatingPosition(position: FloatingPosition): FloatingPosition {
  const maxX = Math.max(FEEDBACK_BUTTON_MARGIN, window.innerWidth - FEEDBACK_BUTTON_SIZE - FEEDBACK_BUTTON_MARGIN);
  const maxY = Math.max(FEEDBACK_BUTTON_MARGIN, window.innerHeight - FEEDBACK_BUTTON_SIZE - FEEDBACK_BUTTON_MARGIN);

  return {
    x: Math.min(Math.max(position.x, FEEDBACK_BUTTON_MARGIN), maxX),
    y: Math.min(Math.max(position.y, FEEDBACK_BUTTON_MARGIN), maxY),
  };
}

export function FeedbackButton() {
  const { user } = useAuth();
  const { data: rolePermsData } = useRolePermissions();
  const dynamicPerms = user?.rol ? rolePermsData?.data?.[user.rol] : undefined;
  const userPerms: readonly string[] = user
    ? (dynamicPerms ?? ROLE_PERMISSIONS[user.rol as AnyRole] ?? [])
    : [];
  const canSubmitFeedback = userPerms.includes("*") || userPerms.includes(PERMISSIONS.FEEDBACK_WRITE);

  const [open, setOpen] = useState(false);
  const [isPreparing, setIsPreparing] = useState(false);
  const [isRetryingCapture, setIsRetryingCapture] = useState(false);
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [capturedMetadata, setCapturedMetadata] = useState<FeedbackMetadata | null>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [position, setPosition] = useState<FloatingPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const createMut = useCreateFeedback();
  const { toast } = useToast();

  const [form, setForm] = useState<{ tipo: Tipo; prioridad: Prioridad; titulo: string; descripcion: string }>({
    tipo: "bug",
    prioridad: "media",
    titulo: "",
    descripcion: "",
  });

  // Ref to hold pre-captured metadata so the screenshot is taken
  // on pointerdown (before Radix dismiss closes any open modal).
  const pendingCaptureRef = useRef<Promise<FeedbackMetadata> | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressOpenRef = useRef(false);

  // Stop Radix's dismiss-layer from seeing clicks on the feedback trigger.
  // Radix adds its document pointerdown/click listeners via setTimeout(0),
  // so our synchronously-registered listeners always run FIRST in bubble phase.
  // React (for portaled content) delegates at document.body in capture phase,
  // which runs before document bubble — so React events are unaffected.
  useEffect(() => {
    function stopIfTrigger(e: Event) {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-feedback-trigger="true"]')) {
        e.stopImmediatePropagation();
      }
    }
    document.addEventListener("pointerdown", stopIfTrigger);
    document.addEventListener("click", stopIfTrigger);
    return () => {
      document.removeEventListener("pointerdown", stopIfTrigger);
      document.removeEventListener("click", stopIfTrigger);
    };
  }, []);

  useEffect(() => {
    const stored = window.localStorage.getItem(FEEDBACK_POSITION_STORAGE_KEY);

    if (!stored) {
      setPosition(getDefaultFloatingPosition());
      return;
    }

    try {
      const parsed = JSON.parse(stored) as Partial<FloatingPosition>;
      if (typeof parsed.x === "number" && typeof parsed.y === "number") {
        setPosition(clampFloatingPosition({ x: parsed.x, y: parsed.y }));
        return;
      }
    } catch {
      // Ignore invalid persisted positions and fall back to the default corner.
    }

    setPosition(getDefaultFloatingPosition());
  }, []);

  useEffect(() => {
    if (!position) return;

    const handleResize = () => {
      setPosition((current) => (current ? clampFloatingPosition(current) : current));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [position]);

  function handlePointerDown() {
    if (isPreparing || open) return;
    // Kick off capture immediately — the DOM still has the open modal
    pendingCaptureRef.current = captureFeedbackMetadata();
  }

  async function handleOpen() {
    if (isPreparing || open) return;
    setIsPreparing(true);
    const capture = pendingCaptureRef.current ?? captureFeedbackMetadata();
    pendingCaptureRef.current = null;
    const metadata = await capture;
    setCapturedMetadata(metadata);
    setIsPreparing(false);
    setOpen(true);
  }

  async function handleRetryCapture() {
    if (isRetryingCapture) return;
    setIsRetryingCapture(true);
    const metadata = await captureFeedbackMetadata();
    setCapturedMetadata(metadata);
    setIsRetryingCapture(false);
  }

  function persistFloatingPosition(nextPosition: FloatingPosition) {
    window.localStorage.setItem(FEEDBACK_POSITION_STORAGE_KEY, JSON.stringify(nextPosition));
  }

  function handleTriggerPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    if (e.button !== 0 || isPreparing || open || !position) return;

    handlePointerDown();
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
      moved: false,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handleTriggerPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== e.pointerId) return;

    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;
    const hasMovedEnough = Math.hypot(deltaX, deltaY) >= FEEDBACK_DRAG_THRESHOLD;

    if (!dragState.moved && !hasMovedEnough) return;

    dragState.moved = true;
    setIsDragging(true);
    setPosition(
      clampFloatingPosition({
        x: dragState.originX + deltaX,
        y: dragState.originY + deltaY,
      })
    );
  }

  function finishDragging(pointerId: number) {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== pointerId) return;

    if (dragState.moved) {
      suppressOpenRef.current = true;
      setPosition((current) => {
        if (!current) return current;
        const nextPosition = clampFloatingPosition(current);
        persistFloatingPosition(nextPosition);
        return nextPosition;
      });
    }

    dragStateRef.current = null;
    setIsDragging(false);
  }

  function handleTriggerPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    finishDragging(e.pointerId);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function handleTriggerPointerCancel(e: React.PointerEvent<HTMLButtonElement>) {
    finishDragging(e.pointerId);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }

  function handleTriggerClick() {
    if (suppressOpenRef.current) {
      suppressOpenRef.current = false;
      return;
    }

    void handleOpen();
  }

  async function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const toProcess = files.slice(0, MAX_ATTACHMENTS - attachments.length);
    const compressed = await Promise.all(toProcess.map((f) => compressImage(f)));
    setAttachments((prev) => [...prev, ...compressed]);
    e.target.value = "";
  }

  function removeAttachment(index: number) {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  }

  function handleClose() {
    setOpen(false);
    setCapturedMetadata(null);
    setAttachments([]);
    setForm({ tipo: "bug", prioridad: "media", titulo: "", descripcion: "" });
  }

  async function handleSubmit() {
    try {
      setIsUploadingAssets(true);

      const metadata: FeedbackMetadata = {
        ...(capturedMetadata ?? {}),
        prioridad: form.prioridad,
      };

      if (metadata.screenshotDataUrl) {
        try {
          metadata.screenshotUrl = await uploadImageDataUrl(metadata.screenshotDataUrl, "feedback-screenshot");
          metadata.screenshotDataUrl = null;
        } catch (error) {
          metadata.captureStatus = "fallback";
          appendCaptureError(metadata, "No se pudo subir la captura principal");
          Sentry.captureException(error, {
            tags: { feature: "feedback", action: "upload_screenshot" },
            extra: { route: location.pathname },
          });
        }
      }

      if (attachments.length > 0) {
        const uploadedAttachmentUrls: string[] = [];
        for (const [index, dataUrl] of attachments.entries()) {
          try {
            const uploadedUrl = await uploadImageDataUrl(dataUrl, `feedback-attachment-${index + 1}`);
            uploadedAttachmentUrls.push(uploadedUrl);
          } catch (error) {
            metadata.captureStatus = "fallback";
            appendCaptureError(metadata, `No se pudo subir el adjunto ${index + 1}`);
            Sentry.captureException(error, {
              tags: { feature: "feedback", action: "upload_attachment" },
              extra: { route: location.pathname, attachmentIndex: index + 1 },
            });
          }
        }

        if (uploadedAttachmentUrls.length > 0) {
          metadata.attachmentUrls = uploadedAttachmentUrls;
        }
        metadata.attachments = undefined;
      }

      await createMut.mutateAsync({
        tipo: form.tipo,
        titulo: form.titulo || undefined,
        descripcion: form.descripcion,
        ruta: location.pathname,
        metadata,
      });

      showSuccessToast(toast, "Reporte enviado", "Lo revisaremos a la brevedad.");
      handleClose();
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: "feedback", action: "submit" },
        extra: {
          route: location.pathname,
          tipo: form.tipo,
          hasScreenshot: Boolean(capturedMetadata?.screenshotDataUrl || capturedMetadata?.screenshotUrl),
          attachmentsCount: attachments.length,
          captureStatus: capturedMetadata?.captureStatus ?? null,
          captureError: capturedMetadata?.captureError ?? null,
        },
      });
      showErrorToast(toast, { action: "enviar el reporte", error });
    } finally {
      setIsUploadingAssets(false);
    }
  }

  if (!canSubmitFeedback) return null;

  return createPortal(
    <>
      {/* stopPropagation on native events prevents Radix dismiss-layer
          (which listens on document bubble) from closing open modals. 
          React's synthetic onClick still fires because React delegates
          on document.body for portaled content, before the event reaches document. */}
      <button
        data-feedback-trigger="true"
        data-feedback-ignore="true"
        type="button"
        aria-label="Enviar feedback"
        title="Enviar feedback"
        onPointerDown={handleTriggerPointerDown}
        onPointerMove={handleTriggerPointerMove}
        onPointerUp={handleTriggerPointerUp}
        onPointerCancel={handleTriggerPointerCancel}
        onClick={handleTriggerClick}
        disabled={isPreparing}
        style={{
          pointerEvents: "auto",
          left: position?.x ?? -9999,
          top: position?.y ?? -9999,
        }}
        className={cn(
          "fixed z-[9999] flex size-8 items-center justify-center rounded-full bg-primary/72 text-primary-foreground shadow-sm transition-[transform,opacity,box-shadow,background-color] duration-200 touch-none select-none",
          "opacity-60 hover:scale-[1.03] hover:bg-primary hover:opacity-100 hover:shadow-md focus-visible:bg-primary focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          isDragging ? "cursor-grabbing scale-[1.02] bg-primary opacity-100 shadow-lg" : "cursor-grab",
          isPreparing && "opacity-85"
        )}
      >
        {isPreparing ? <RefreshCw className="size-3.5 animate-spin" /> : <MessageSquarePlus className="size-3.5" />}
        <span className="sr-only">{isPreparing ? "Capturando feedback" : "Abrir formulario de feedback"}</span>
      </button>

      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-lg [&>button]:hidden" data-feedback-ignore="true">
          <DialogHeader className="sr-only">
            <DialogTitle>Nuevo reporte de feedback</DialogTitle>
            <DialogDescription>
              Completá el formulario para enviar un reporte con descripción, tipo, prioridad y captura de pantalla opcional.
            </DialogDescription>
          </DialogHeader>

          {/* ── Header ── */}
          <div className="-mx-6 -mt-6 px-6 py-4 border-b flex items-center justify-between bg-muted/40">
            <div>
              <p className="font-semibold text-base">Nuevo reporte</p>
              <p className="text-muted-foreground text-xs mt-0.5">Contanos qué encontraste o qué mejorarías</p>
            </div>
            <DialogClose className="rounded-lg p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
              <X className="size-4" />
            </DialogClose>
          </div>

          {/* ── Tipo + Prioridad ── */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v as Tipo })}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPO_OPTIONS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Prioridad</Label>
              <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v as Prioridad })}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORIDAD_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      <span className="flex items-center gap-2">
                        <span className={cn("inline-block size-2 rounded-full shrink-0", p.color)} />
                        {p.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Título ── */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Título</Label>
            <Input
              value={form.titulo}
              onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              placeholder="Resumen breve del problema o sugerencia"
              className="h-9"
            />
          </div>

          {/* ── Descripción ── */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">
              Descripción <span className="text-destructive normal-case">*</span>
            </Label>
            <Textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="¿Qué estabas haciendo? ¿Qué esperabas que pasara? ¿Qué pasó en cambio?"
              rows={3}
            />
          </div>

          {/* ── Captura de pantalla ── */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">Captura de pantalla</Label>
              <button
                type="button"
                onClick={() => void handleRetryCapture()}
                disabled={isRetryingCapture}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
              >
                <RefreshCw className={cn("size-3", isRetryingCapture && "animate-spin")} />
                Volver a capturar
              </button>
            </div>
            {capturedMetadata?.screenshotDataUrl ? (
              <div className="overflow-hidden rounded-lg border bg-muted/10">
                <img
                  src={capturedMetadata.screenshotDataUrl}
                  alt="Captura de pantalla"
                  className="block max-h-40 w-full object-cover object-top"
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => void handleRetryCapture()}
                disabled={isRetryingCapture}
                className="w-full flex flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-5 text-xs text-muted-foreground hover:border-muted-foreground/50 transition-colors disabled:opacity-50"
              >
                <Camera className="size-5" />
                {isRetryingCapture ? "Capturando..." : "Sin captura — clic para intentar de nuevo"}
              </button>
            )}
          </div>

          {/* ── Adjuntos ── */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                Imágenes adicionales <span className="normal-case font-normal">({attachments.length}/{MAX_ATTACHMENTS})</span>
              </Label>
              {attachments.length < MAX_ATTACHMENTS && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ImagePlus className="size-3" /> Agregar
                </button>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => void handleFilesSelected(e)}
            />
            {attachments.length > 0 ? (
              <div className="flex gap-2 flex-wrap">
                {attachments.map((src, i) => (
                  <div key={src} className="relative group">
                    <img src={src} alt={`Adjunto ${i + 1}`} className="size-16 object-cover rounded-md border" />
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="size-2.5" />
                    </button>
                  </div>
                ))}
                {attachments.length < MAX_ATTACHMENTS && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="size-16 rounded-md border-2 border-dashed border-muted-foreground/25 flex items-center justify-center text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground transition-colors"
                  >
                    <ImagePlus className="size-5" />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed px-4 py-3 text-xs text-muted-foreground hover:border-muted-foreground/50 transition-colors"
              >
                <ImagePlus className="size-3.5" />
                Adjuntar imágenes
              </button>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="-mx-6 -mb-6 px-6 py-4 border-t bg-muted/30 flex items-center justify-between gap-3">
            <span className="text-xs text-muted-foreground truncate">
              <code className="bg-muted rounded px-1 text-[11px]">{location.pathname}</code>
            </span>
            <div className="flex gap-2 shrink-0">
              <Button variant="ghost" size="sm" onClick={handleClose}>Cancelar</Button>
              <Button
                size="sm"
                onClick={() => void handleSubmit()}
                disabled={!form.descripcion || createMut.isPending || isPreparing || isUploadingAssets}
                className="gap-1.5"
              >
                <Send className="size-3.5" />
                {createMut.isPending || isUploadingAssets ? "Enviando..." : "Enviar"}
              </Button>
            </div>
          </div>

        </DialogContent>
      </Dialog>
    </>,
    document.body
  );
}
